// Governance Command Center — Admin / GRC Secretariat dashboard widgets
// that close the 4 spec gaps not covered by the existing GrcDashboardPage:
//   1. Governance Scorecard (composite RAG + 4 sub-scores + 8-week sparklines)
//   2. Risks by Division (horizontal bar chart)
//   3. High-risk Operations (OH × Division concentration)
//   4. Unresolved Deficiencies + Repeated Non-compliance escalations

import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHGS } from '../context/HGSContext';
import { ORG_TREE, lineageForDept, resolveDeptId } from '../data/orgTree';
import { computeResidualScore, bandFor } from '../data/riskUniverse';
import { TrendingUp, TrendingDown, AlertTriangle, ShieldCheck, FileSignature, Activity, Layers, MapPin, MessageSquare, RotateCw, Award } from 'lucide-react';
import { cn } from '@/lib/utils';

const RAG_TONE = {
  Green:  { bg: 'bg-emerald-500',  text: 'text-emerald-700', soft: 'bg-emerald-50 border-emerald-200', label: 'Healthy' },
  Amber:  { bg: 'bg-amber-500',    text: 'text-amber-700',   soft: 'bg-amber-50 border-amber-200',     label: 'Watch' },
  Red:    { bg: 'bg-red-600',      text: 'text-red-700',     soft: 'bg-red-50 border-red-200',         label: 'Critical' },
};

const ragFor = (score) => score >= 75 ? 'Green' : score >= 50 ? 'Amber' : 'Red';

// Synthetic 8-week trend — drifts within ±12 around the current value (demo only).
// Deterministic per `seed` so re-renders don't flicker.
function makeTrend(current, seed = 1) {
  const out = [];
  let v = Math.max(20, current - 8 - (seed % 5));
  for (let i = 0; i < 8; i += 1) {
    out.push(Math.max(0, Math.min(100, v)));
    v += ((seed * (i + 1)) % 7) - 3;
  }
  out.push(current); // last point = today's value
  return out;
}

function Sparkline({ data, color = '#0F172A', height = 28, width = 96 }) {
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const step = width / (data.length - 1);
  const points = data.map((v, i) => `${(i * step).toFixed(1)},${(height - ((v - min) / range) * (height - 2) - 1).toFixed(1)}`).join(' ');
  const last = data[data.length - 1];
  const prev = data[data.length - 2] ?? last;
  const delta = last - prev;
  return (
    <div className="inline-flex items-center gap-1.5">
      <svg width={width} height={height} className="overflow-visible">
        <polyline fill="none" stroke={color} strokeWidth="1.5" points={points} />
        <circle cx={width} cy={height - ((last - min) / range) * (height - 2) - 1} r="2" fill={color} />
      </svg>
      <span className={cn('text-[10px] font-bold inline-flex items-center gap-0.5',
        delta > 0 ? 'text-emerald-600' : delta < 0 ? 'text-red-600' : 'text-slate-400')}>
        {delta > 0 ? <TrendingUp className="w-2.5 h-2.5" /> : delta < 0 ? <TrendingDown className="w-2.5 h-2.5" /> : null}
        {delta > 0 ? '+' : ''}{delta.toFixed(0)}
      </span>
    </div>
  );
}

export default function GovernanceCommandCenter() {
  const navigate = useNavigate();
  const { riskUniverse, masters, formResponses, countermeasures, evaluations } = useHGS();

  // ── Risk posture by division ───────────────────────────────────────────
  const byDivision = useMemo(() => {
    const out = {};
    Object.values(ORG_TREE).forEach((oh) => {
      Object.values(oh.divisions).forEach((div) => {
        out[div.id] = { id: div.id, label: div.label, ohId: oh.id, ohLabel: oh.label, color: oh.color, high: 0, medium: 0, watch: 0, low: 0, total: 0 };
      });
    });
    // Map each enriched risk to its OH (worst residual), and from that OH to ALL its divisions
    riskUniverse.forEach((r) => {
      const { residual } = computeResidualScore(r);
      const band = bandFor(residual);
      const ohId = r.ownerOh || (r.assignedOhs && r.assignedOhs[0]) || null;
      if (!ohId || !ORG_TREE[ohId]) return;
      Object.values(ORG_TREE[ohId].divisions).forEach((div) => {
        const slot = out[div.id];
        slot.total += 1;
        if (band === 'High') slot.high += 1;
        else if (band === 'Medium') slot.medium += 1;
        else if (band === 'Watch') slot.watch += 1;
        else slot.low += 1;
      });
    });
    return Object.values(out).sort((a, b) => (b.high - a.high) || (b.medium - a.medium) || b.total - a.total);
  }, [riskUniverse]);

  // ── High-risk operations (OH × Division concentration of High band) ────
  const highRiskOps = useMemo(() => {
    return byDivision.filter((d) => d.high > 0).slice(0, 6);
  }, [byDivision]);

  // ── Compliance numbers ─────────────────────────────────────────────────
  const compliance = useMemo(() => {
    const stats = (key) => {
      const rows = (masters?.[key] || []).filter((r) => r.active !== false);
      const idField = key === 'fraud' ? 'controlId' : 'questionId';
      let answered = 0, withEvidence = 0, gaps = 0;
      rows.forEach((r) => {
        const resp = formResponses?.[`${key}:${r[idField]}`];
        const v = resp?.values || {};
        if (resp && v.responseType) answered += 1;
        if (resp && v.responseType && v.evidenceFile) withEvidence += 1;
        if (['No', 'N/G', 'N/A'].includes(v.responseType)) gaps += 1;
      });
      return {
        total: rows.length,
        completionPct: rows.length === 0 ? 0 : Math.round((answered / rows.length) * 100),
        evidencePct:   answered === 0 ? 0 : Math.round((withEvidence / answered) * 100),
        gaps,
        answered,
      };
    };
    const p = stats('pcc'), f = stats('fraud'), d = stats('dlc');
    const overallCompletion = [p, f, d].filter((s) => s.total > 0);
    const completionPct = overallCompletion.length === 0 ? 0 :
      Math.round(overallCompletion.reduce((a, s) => a + s.completionPct, 0) / overallCompletion.length);
    const evidencePct = overallCompletion.length === 0 ? 0 :
      Math.round(overallCompletion.reduce((a, s) => a + s.evidencePct, 0) / overallCompletion.length);
    return { pcc: p, fraud: f, dlc: d, completionPct, evidencePct };
  }, [masters, formResponses]);

  // ── DLC unresolved deficiencies (deficiency=Yes && !evidenceFile) ──────
  const unresolvedDeficiencies = useMemo(() => {
    const dlcRows = (masters?.dlc || []).filter((r) => r.active !== false);
    return dlcRows
      .map((r) => {
        const resp = formResponses?.[`dlc:${r.questionId}`];
        const v = resp?.values || {};
        if (v.deficiency !== 'Yes') return null;
        if (v.evidenceFile) return null;
        const deptId = resolveDeptId(r.assignedDepartment);
        const lineage = deptId ? lineageForDept(deptId) : null;
        return {
          id: r.questionId,
          question: r.declarationQuestion || r.checkItem,
          dept: lineage?.deptLabel || r.assignedDepartment,
          oh: lineage?.ohLabel || '—',
          remarks: v.currentSituation || '—',
        };
      })
      .filter(Boolean);
  }, [masters, formResponses]);

  // ── Repeated non-compliance — departments with ≥2 gap responses across modules
  const repeatedNonCompliance = useMemo(() => {
    const byDept = {};
    ['pcc', 'fraud', 'dlc'].forEach((key) => {
      const rows = (masters?.[key] || []).filter((r) => r.active !== false);
      const idField = key === 'fraud' ? 'controlId' : 'questionId';
      rows.forEach((r) => {
        const resp = formResponses?.[`${key}:${r[idField]}`];
        const v = resp?.values || {};
        if (!['No', 'N/G', 'N/A'].includes(v.responseType)) return;
        const deptId = resolveDeptId(r.assignedDepartment);
        if (!deptId) return;
        if (!byDept[deptId]) {
          const lineage = lineageForDept(deptId);
          byDept[deptId] = { deptId, deptLabel: lineage?.deptLabel || deptId, divLabel: lineage?.divLabel || '—', ohLabel: lineage?.ohLabel || '—', counts: { pcc: 0, fraud: 0, dlc: 0 }, total: 0 };
        }
        byDept[deptId].counts[key] += 1;
        byDept[deptId].total += 1;
      });
    });
    return Object.values(byDept).filter((d) => d.total >= 2).sort((a, b) => b.total - a.total).slice(0, 6);
  }, [masters, formResponses]);

  // ── Governance scorecard composite ─────────────────────────────────────
  const scorecard = useMemo(() => {
    const totalRisks = riskUniverse.length;
    const highCount = riskUniverse.filter((r) => bandFor(computeResidualScore(r).residual) === 'High').length;
    // Risk Posture — 100 = no High, decays as % High climbs
    const riskScore = Math.max(0, Math.round(100 - (highCount / Math.max(totalRisks, 1)) * 200));

    const evidenceScore = compliance.evidencePct;
    const complianceScore = compliance.completionPct;

    const overdue = (countermeasures || []).filter((c) => {
      if (c.status === 'Closed') return false;
      const due = c.dueDate || c.targetDate;
      return due && new Date(due) < new Date();
    }).length;
    const openCms = (countermeasures || []).filter((c) => c.status !== 'Closed').length;
    const escalationScore = openCms === 0 ? 100 : Math.max(0, Math.round(100 - (overdue / openCms) * 150));

    const overall = Math.round((riskScore * 0.3 + complianceScore * 0.3 + evidenceScore * 0.2 + escalationScore * 0.2));
    return {
      overall, rag: ragFor(overall),
      sub: [
        { key: 'risk',       label: 'Risk Posture',       value: riskScore,       icon: ShieldCheck,    trend: makeTrend(riskScore, 7) },
        { key: 'compliance', label: 'Compliance',          value: complianceScore, icon: Activity,       trend: makeTrend(complianceScore, 3) },
        { key: 'evidence',   label: 'Evidence Completeness', value: evidenceScore, icon: FileSignature,  trend: makeTrend(evidenceScore, 11) },
        { key: 'escalation', label: 'Escalation Health',  value: escalationScore, icon: AlertTriangle,  trend: makeTrend(escalationScore, 13) },
      ],
      trend: makeTrend(overall, 5),
    };
  }, [riskUniverse, compliance, countermeasures]);

  return (
    <div className="space-y-5" data-testid="governance-command-center">

      {/* ── Governance Scorecard (composite RAG + 4 sub-scores + sparkline trends) ── */}
      <div className="rounded-xl border border-slate-200 overflow-hidden bg-white" data-testid="governance-scorecard">
        <div className="bg-gradient-to-r from-slate-900 to-[#0a1830] text-white p-5 flex items-center gap-5">
          <div className="w-14 h-14 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center">
            <Award className="w-6 h-6 text-amber-400" />
          </div>
          <div className="flex-1">
            <div className="text-[10px] font-bold uppercase tracking-wider text-amber-400">Governance Scorecard · Enterprise composite</div>
            <div className="flex items-baseline gap-3">
              <span className="text-5xl font-bold tabular-nums" data-testid="scorecard-overall">{scorecard.overall}</span>
              <span className={cn('px-2.5 py-0.5 rounded text-[10px] font-bold uppercase', RAG_TONE[scorecard.rag].bg, 'text-white')}>{RAG_TONE[scorecard.rag].label}</span>
              <span className="text-[11px] text-slate-300">/ 100 · weighted risk × compliance × evidence × escalation</span>
            </div>
          </div>
          <div className="hidden md:block">
            <Sparkline data={scorecard.trend} color="#FACC15" width={140} height={36} />
            <p className="text-[10px] text-slate-400 text-right mt-1">8-week trend</p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-slate-100 border-t border-slate-100">
          {scorecard.sub.map((s) => {
            const rag = ragFor(s.value);
            const tone = RAG_TONE[rag];
            return (
              <div key={s.key} className="p-4" data-testid={`scorecard-sub-${s.key}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 inline-flex items-center gap-1.5">
                    <s.icon className="w-3 h-3" />
                    {s.label}
                  </span>
                  <span className={cn('w-1.5 h-1.5 rounded-full', tone.bg)} />
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="text-2xl font-bold tabular-nums text-slate-900">{s.value}<span className="text-sm text-slate-400">%</span></span>
                  <Sparkline data={s.trend} color="#0F172A" />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Risks by Division ──────────────────────────────────────────── */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="risks-by-division">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
          <Layers className="w-4 h-4 text-slate-500" />
          <div>
            <h3 className="text-sm font-bold text-slate-900">Risks by Division</h3>
            <p className="text-[11px] text-slate-500">Horizontal stack · High → Medium → Watch → Low · sorted by High concentration · risks fan out to all divisions under their owning Operating Head</p>
          </div>
        </div>
        <div className="p-5 space-y-2.5">
          {byDivision.filter((d) => d.total > 0).map((d) => {
            const total = d.total || 1;
            return (
              <div key={d.id} className="flex items-center gap-3" data-testid={`risk-div-${d.id}`}>
                <div className="w-44 flex-shrink-0 min-w-0">
                  <div className="text-xs font-semibold text-slate-900 truncate">{d.label}</div>
                  <div className="text-[10px] text-slate-500">{d.ohLabel}</div>
                </div>
                <div className="flex-1 h-5 bg-slate-50 rounded-md overflow-hidden flex">
                  {d.high > 0     && <div className="bg-red-600 h-full"     style={{ width: `${(d.high   / total) * 100}%` }} title={`High: ${d.high}`} />}
                  {d.medium > 0   && <div className="bg-amber-500 h-full"   style={{ width: `${(d.medium / total) * 100}%` }} title={`Medium: ${d.medium}`} />}
                  {d.watch > 0    && <div className="bg-yellow-300 h-full"  style={{ width: `${(d.watch  / total) * 100}%` }} title={`Watch: ${d.watch}`} />}
                  {d.low > 0      && <div className="bg-emerald-400 h-full" style={{ width: `${(d.low    / total) * 100}%` }} title={`Low: ${d.low}`} />}
                </div>
                <div className="w-32 text-right flex items-center justify-end gap-1.5 text-[10px] font-mono">
                  {d.high > 0   && <span className="px-1 py-0.5 rounded bg-red-100 text-red-800 font-bold">H {d.high}</span>}
                  {d.medium > 0 && <span className="px-1 py-0.5 rounded bg-amber-100 text-amber-800 font-bold">M {d.medium}</span>}
                  <span className="text-slate-700 font-bold">{d.total}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── High-Risk Operations + Escalations row ─────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="rounded-xl border border-red-200 bg-red-50/30 overflow-hidden" data-testid="high-risk-ops">
          <div className="px-5 py-4 border-b border-red-100 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-red-600" />
            <div>
              <h3 className="text-sm font-bold text-slate-900">High-Risk Operations</h3>
              <p className="text-[11px] text-slate-500">Divisions with active High-band risks · prioritised review queue</p>
            </div>
          </div>
          {highRiskOps.length === 0 ? (
            <div className="p-8 text-center" data-testid="high-risk-empty">
              <ShieldCheck className="w-8 h-8 mx-auto text-emerald-300 mb-2" />
              <p className="text-sm font-semibold text-slate-700">No High-band risks across operations</p>
            </div>
          ) : (
            <div className="divide-y divide-red-100">
              {highRiskOps.map((d, i) => (
                <button
                  key={d.id}
                  onClick={() => navigate('/risk')}
                  data-testid={`hro-${d.id}`}
                  className="w-full px-5 py-3 flex items-center gap-3 hover:bg-red-50/50 transition text-left"
                >
                  <span className="w-7 h-7 rounded-md bg-red-100 text-red-800 text-[11px] font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold text-slate-900 truncate">{d.label}</div>
                    <div className="text-[10px] text-slate-500">{d.ohLabel}</div>
                  </div>
                  <span className="px-1.5 py-0.5 rounded bg-red-600 text-white text-[10px] font-bold">{d.high} HIGH</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-violet-200 bg-violet-50/30 overflow-hidden" data-testid="unresolved-deficiencies">
          <div className="px-5 py-4 border-b border-violet-100 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-violet-700" />
            <div>
              <h3 className="text-sm font-bold text-slate-900">Unresolved DLC Deficiencies</h3>
              <p className="text-[11px] text-slate-500">deficiency=Yes &amp; no evidence file uploaded yet</p>
            </div>
          </div>
          {unresolvedDeficiencies.length === 0 ? (
            <div className="p-8 text-center" data-testid="unresolved-deficiencies-empty">
              <ShieldCheck className="w-8 h-8 mx-auto text-emerald-300 mb-2" />
              <p className="text-sm font-semibold text-slate-700">No unresolved deficiencies</p>
            </div>
          ) : (
            <div className="divide-y divide-violet-100 max-h-64 overflow-y-auto">
              {unresolvedDeficiencies.slice(0, 8).map((d) => (
                <button
                  key={d.id}
                  onClick={() => navigate('/dlc')}
                  data-testid={`unresolved-def-${d.id}`}
                  className="w-full px-5 py-3 flex items-start gap-3 hover:bg-violet-50/50 transition text-left"
                >
                  <span className="font-mono text-[10px] text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded flex-shrink-0">{d.id}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-slate-900 line-clamp-2">{d.question}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{d.dept} · {d.oh}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Repeated non-compliance heatmap ────────────────────────────── */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="repeated-noncompliance">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
          <RotateCw className="w-4 h-4 text-amber-700" />
          <div>
            <h3 className="text-sm font-bold text-slate-900">Repeated Non-Compliance</h3>
            <p className="text-[11px] text-slate-500">Departments with ≥2 non-compliant responses (No / N/G / N/A) across modules</p>
          </div>
        </div>
        {repeatedNonCompliance.length === 0 ? (
          <div className="p-8 text-center" data-testid="repeated-noncompliance-empty">
            <ShieldCheck className="w-8 h-8 mx-auto text-emerald-300 mb-2" />
            <p className="text-sm font-semibold text-slate-700">No departments with repeated non-compliance</p>
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-5 py-2 font-bold uppercase tracking-wider text-[10px] text-slate-600">Department</th>
                <th className="text-left px-5 py-2 font-bold uppercase tracking-wider text-[10px] text-slate-600">Division · OH</th>
                <th className="text-right px-3 py-2 font-bold uppercase tracking-wider text-[10px] text-slate-600">PCC</th>
                <th className="text-right px-3 py-2 font-bold uppercase tracking-wider text-[10px] text-slate-600">Fraud</th>
                <th className="text-right px-3 py-2 font-bold uppercase tracking-wider text-[10px] text-slate-600">DLC</th>
                <th className="text-right px-3 py-2 font-bold uppercase tracking-wider text-[10px] text-slate-600">Total</th>
              </tr>
            </thead>
            <tbody>
              {repeatedNonCompliance.map((d) => (
                <tr key={d.deptId} className="border-b border-slate-100 hover:bg-slate-50 transition" data-testid={`rnc-${d.deptId}`}>
                  <td className="px-5 py-2 font-semibold text-slate-800">{d.deptLabel}</td>
                  <td className="px-5 py-2 text-[10px] text-slate-500">{d.divLabel} · {d.ohLabel}</td>
                  <td className="text-right px-3 py-2 font-mono">{d.counts.pcc > 0 ? <span className="font-bold text-violet-700">{d.counts.pcc}</span> : <span className="text-slate-300">—</span>}</td>
                  <td className="text-right px-3 py-2 font-mono">{d.counts.fraud > 0 ? <span className="font-bold text-sky-700">{d.counts.fraud}</span> : <span className="text-slate-300">—</span>}</td>
                  <td className="text-right px-3 py-2 font-mono">{d.counts.dlc > 0 ? <span className="font-bold text-amber-700">{d.counts.dlc}</span> : <span className="text-slate-300">—</span>}</td>
                  <td className="text-right px-3 py-2 font-mono">
                    <span className={cn('px-2 py-0.5 rounded text-[10px] font-bold',
                      d.total >= 5 ? 'bg-red-600 text-white' : d.total >= 3 ? 'bg-amber-500 text-white' : 'bg-slate-200 text-slate-800')}>
                      {d.total}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
